import { memo, MouseEvent } from "react";
import ReactSlider, { Settings, CustomArrowProps } from "react-slick";
import { Image as ImageProps } from "src/types/storefront";
import classNames from "src/helpers/classNames";
import useRtl from "src/hooks/useRtl";
import { MagnifyingGlassPlusIcon } from "@heroicons/react/24/outline";
import useLanguage from "src/hooks/useLanguage";
import Image from "next/image";
import { ProductType } from "src/types/product-types/ProductType";

type Props = {
  images: ImageProps[];
  activeImage?: number;
  setFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
  onSlideClick?(event: MouseEvent<HTMLDivElement>, index: number): void;
  className?: string;
  imageClassName?: string;
  imageContainerClassName?: string;
  withFrenchTechnologyLogo?: boolean;
};
function NextArrow({ className = "", onClick }: CustomArrowProps) {
  const rtl = useRtl();

  return (
    <div
      className={classNames(
        className,
        "z-10 !flex !h-12 !w-12 items-center justify-center before:!text-5xl",
        rtl ? "!left-3" : "!right-3"
      )}
      onClick={onClick}
    />
  );
}

function PrevArrow({ className = "", onClick }: CustomArrowProps) {
  const rtl = useRtl();

  return (
    <div
      className={classNames(
        className,
        "z-10 !flex !h-12 !w-12 items-center justify-center before:!text-5xl",
        rtl ? "!right-3" : "!left-3"
      )}
      onClick={onClick}
    />
  );
}

const settings: Settings = {
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  accessibility: true,
  draggable: true,
  easing: "linear",
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
};

function Slider({
  images,
  activeImage = 0,
  onSlideClick,
  className = "",
  imageContainerClassName = "",
  imageClassName = "",
  setFullscreen,
  withFrenchTechnologyLogo = true,
}: Props) {
  const language = useLanguage();

  return (
    <div className="overflow-hidden">
      <ReactSlider
        {...settings}
        initialSlide={activeImage}
        className={className}
      >
        {images.map((image, index: number) => (
          <div
            key={index}
            onClick={
              onSlideClick &&
              ((event) => {
                onSlideClick(event, index);
              })
            }
            className={classNames("relative", imageContainerClassName)}
          >
            <button
              type="button"
              aria-label="Open image in fullscreen mode"
              className="absolute right-4 top-4 flex h-12 w-12 rounded-full bg-white opacity-80 hover:opacity-100 focus:opacity-100"
              onClick={() => setFullscreen(true)}
            >
              <MagnifyingGlassPlusIcon className="m-auto w-6" />
            </button>

            <Image
              priority
              className={classNames(imageClassName)}
              src={image.url}
              alt={
                typeof image.altText === "string"
                  ? image.altText
                  : "product image"
              }
              sizes="38rem"
              width={0}
              height={0}
            />
            {withFrenchTechnologyLogo && (
              <Image
                src={`/images/product-page/product-image/french-technology-${language.value}.png`}
                className="absolute bottom-4 right-6 flex h-8 w-28"
                alt="French Technology"
                width={0}
                height={0}
                sizes="8rem"
              />
            )}
          </div>
        ))}
      </ReactSlider>
    </div>
  );
}

export default memo(Slider);
